/* eslint-disable react-hooks/exhaustive-deps */
// SellerNetSheet/hooks/useSellerNetForm.js
import { useCallback, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { INITIAL_VALUES } from "../sellerNet.shared";

/**
 * useSellerNetForm
 * ----------------
 * This hook centralizes ALL RHF wiring:
 * - useForm() initialization
 * - useFieldArray() setup for each meta array
 * - refs that help guard effects when we programmatically populate values
 * - replaceArray helper to reduce boilerplate and bugs
 */
export const useSellerNetForm = () => {
  // 1) Create RHF form methods
  const methods = useForm({ defaultValues: INITIAL_VALUES, mode: "onBlur" });
  const { control, setValue } = methods;

  // 2) Create RHF field arrays (these manage dynamic list rows)
  const arrays = {
    sellerClosingCost: useFieldArray({
      control,
      name: "sellerClosingCostMeta",
    }),
    closingCost: useFieldArray({ control, name: "closingCostMeta" }),
    sellerOtherCost: useFieldArray({ control, name: "sellerOtherCostMeta" }),
    compensation: useFieldArray({ control, name: "compensationMeta" }),
    otherCost: useFieldArray({ control, name: "otherCostMeta" }),
  };

  /**
   * 3) Refs (guards)
   * - loadedExistingRef: remember which calcId was loaded
   * - loadingExistingRef: prevent concurrent loads
   * - isPopulatingRef: tells effects "skip" while we are doing bulk setValue()
   */
  const refs = {
    loadedExistingRef: useRef(null),
    loadingExistingRef: useRef(false),
    isPopulatingRef: useRef(false),
  };

  // Map our “short names” to actual RHF field names
  const nameToField = {
    sellerClosingCost: "sellerClosingCostMeta",
    closingCost: "closingCostMeta",
    sellerOtherCost: "sellerOtherCostMeta",
    compensation: "compensationMeta",
    otherCost: "otherCostMeta",
  };

  /**
   * 4) replaceArray(name, data)
   * - Prefer fieldArray.replace() for performance & correct RHF internal state
   * - fallback to setValue if needed
   */
  const replaceArray = useCallback(
    (name, data) => {
      const arr = arrays[name];
      const fieldName = nameToField[name] || name;
      const safeData = Array.isArray(data) ? data : [];

      if (arr?.replace) arr.replace(safeData);
      else setValue(fieldName, safeData);
    },
    [arrays, setValue]
  );

  return { methods, arrays, refs, replaceArray };
};
